import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { UserContext } from '../UserContext';
import webRTCService from '../services/WebRTCService';
import callNotificationService from '../services/callNotificationService';
import { db } from '../config/firebasedeconfig';
import { doc, getDoc, query, collection, where, getDocs, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getLevelTitle } from './levelUtils';
import Lottie from 'react-lottie';
import pulseAnimation from '../lotties/84036-circle-animation.json';
import spiroAnimation from '../lotties/6042-spiro.json';
import './VideoCallManager.scss';
import { formatTimestampShort, getCountryFlag } from '../utils/timestampUtils';
import Flag from 'react-world-flags';

// Composant pour la notification d'appel entrant
const IncomingCallNotification = ({
  caller,
  callData,
  onAccept,
  onReject,
  isVisible,
  ringDuration = 0
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [callerMessageCount, setCallerMessageCount] = useState(0);
  const [showParticles, setShowParticles] = useState(true);

  // Options pour l'animation Lottie
  const pulseOptions = {
    loop: true,
    autoplay: true,
    animationData: pulseAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };

  const spiroOptions = {
    loop: true,
    autoplay: true,
    animationData: spiroAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      // Activer les particules après un court délai
      setTimeout(() => setShowParticles(true), 300);
    }
  }, [isVisible]);

  // Récupérer le messageCount du caller
  useEffect(() => {
    const fetchCallerMessageCount = async () => {
      if (!caller?.userId && !caller?.uid) return;

      try {
        const callerId = caller.userId || caller.uid;
        const userDocRef = doc(db, 'users', callerId);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          let messageCount = userData.messageCount || 0;

          // Si pas de messageCount stocké, calculer depuis les posts du forum
          if (!messageCount) {
            const postsQuery = query(collection(db, 'posts'), where('authorId', '==', callerId));
            const postsSnapshot = await getDocs(postsQuery);
            messageCount = postsSnapshot.size;

            // Compter aussi les réponses dans les posts
            for (const postDoc of postsSnapshot.docs) {
              const repliesQuery = query(collection(db, 'posts', postDoc.id, 'replies'));
              const repliesSnapshot = await getDocs(repliesQuery);
              messageCount += repliesSnapshot.size;
            }
          }

          setCallerMessageCount(messageCount);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du messageCount du caller:', error);
        setCallerMessageCount(0);
      }
    };

    if (caller) {
      fetchCallerMessageCount();
    }
  }, [caller]);

  if (!isVisible) return null;

  return (
    <div className={`mf-incoming-call-overlay ${isAnimating ? 'animate-in' : ''}`}>
      {/* Animations de fond avec Lottie */}
      <div className="mf-background-animations">
        <div className="mf-lottie-spiro">
          <Lottie options={spiroOptions} height={600} width={600} />
        </div>
        {showParticles && (
          <div className="mf-particles-container">
            {[...Array(20)].map((_, i) => (
              <div key={i} className={`mf-particle particle-${i}`} />
            ))}
          </div>
        )}
      </div>

      <div className="mf-incoming-call-modal">
        {/* Animation Lottie derrière l'avatar */}
        <div className="mf-lottie-pulse">
          <Lottie options={pulseOptions} height={400} width={400} />
        </div>

        {/* Effet de pulsation lumineux */}
        <div className="mf-pulse-rings">
          <div className="mf-pulse-ring ring-1"></div>
          <div className="mf-pulse-ring ring-2"></div>
          <div className="mf-pulse-ring ring-3"></div>
        </div>

        {/* Avatar de l'appelant avec système de niveau */}
        <div className="mf-caller-avatar">
          {/* 🔧 AVATAR SIMPLIFIÉ pour éviter les conflits CSS du forum */}
          <div className="mf-avatar-container">
            <div className="mf-avatar-glow-outer"></div>
            <img
              src={caller?.avatar || '/images/defaultAvatar.png'}
              alt={caller?.username || 'Appelant'}
              className="mf-simple-avatar"
              onError={(e) => {
                e.target.src = '/images/defaultAvatar.png';
              }}
            />
            <div className="mf-avatar-glow"></div>
            {/* Badge de niveau */}
            <div className="mf-level-badge">
              <span className="mf-level-number">{Math.floor(callerMessageCount / 100) + 1}</span>
            </div>
          </div>
        </div>

        {/* Informations de l'appelant */}
        <div className="mf-caller-info">
          <h2 className="mf-caller-name">{caller?.username || 'Utilisateur inconnu'}</h2>
          <div className="mf-caller-level">
            <span className="mf-level-icon">⭐</span>
            {getLevelTitle(callerMessageCount)} • {callerMessageCount} messages
          </div>
          <p className="mf-call-status">
            <span className="mf-status-icon">📹</span>
            Appel vidéo entrant...
          </p>
          <div className="mf-ring-timer">
            <div className="mf-timer-bar" style={{ width: `${Math.min((ringDuration / 30000) * 100, 100)}%` }}></div>
            <span className="mf-timer-text">{Math.floor(ringDuration / 1000)}s</span>
          </div>
        </div>

        {/* Boutons d'action avec animations améliorées */}
        <div className="mf-incoming-actions">
          <button
            className="mf-btn mf-btn-reject"
            onClick={onReject}
            title="Refuser l'appel"
          >
            <div className="mf-btn-bg"></div>
            <span className="mf-btn-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.21c1.21.49 2.53.76 3.89.76a1 1 0 011 1v3.4a1 1 0 01-1 1C10.07 22 2 13.93 2 4a1 1 0 011-1h3.4a1 1 0 011 1c0 1.36.26 2.68.76 3.89a1 1 0 01-.21 1.11l-2.2 2.2z"
                  fill="currentColor"
                  transform="rotate(135 12 12)" />
              </svg>
            </span>
            <div className="mf-btn-ripple"></div>
          </button>

          <button
            className="mf-btn mf-btn-accept"
            onClick={onAccept}
            title="Accepter l'appel"
          >
            <div className="mf-btn-bg"></div>
            <span className="mf-btn-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M17 10.5V7a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1v-3.5l4 4v-11l-4 4z"
                  fill="currentColor" />
              </svg>
            </span>
            <div className="mf-btn-ripple"></div>
          </button>
        </div>

        {/* Indicateur de sonnerie */}
        <div className="mf-sound-indicator">
          <span className="mf-sound-wave wave-1"></span>
          <span className="mf-sound-wave wave-2"></span>
          <span className="mf-sound-wave wave-3"></span>
        </div>
      </div>
    </div>
  );
};

// Composant principal de gestion des appels vidéo
const VideoCallManager = ({
  isOpen,
  onClose,
  targetUser = null,
  mode = 'outgoing' // 'outgoing', 'incoming', 'active'
}) => {
  // Log de débogage pour tracer l'initialisation
  console.log('[VideoCallManager] 🚀 INITIALISATION:', {
    isOpen,
    mode,
    targetUser: targetUser?.username || 'null',
    timestamp: new Date().toISOString()
  });

  const { userId: currentUserId, username: currentUsername, avatar: currentAvatar } = useContext(UserContext);

  console.log('[VideoCallManager] 👤 UserContext récupéré:', {
    currentUserId,
    currentUsername,
    currentAvatar: currentAvatar ? 'présent' : 'absent',
    timestamp: new Date().toISOString()
  });

  // États principaux
  const [callState, setCallState] = useState('idle'); // idle, waiting, ringing, connecting, connected, ended
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState('good'); // good, fair, poor
  const [callDuration, setCallDuration] = useState(0);
  const [waitingDuration, setWaitingDuration] = useState(0); // Nouveau : temps d'attente pour l'appelant
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);

  // 🔧 NOUVEAU : État pour forcer l'affichage de l'interface vidéo
  const [forceShowInterface, setForceShowInterface] = useState(false);

  // États pour le système de niveau
  const [targetUserMessageCount, setTargetUserMessageCount] = useState(0);
  const [currentUserMessageCount, setCurrentUserMessageCount] = useState(0);

  // États pour appel entrant
  const [incomingCall, setIncomingCall] = useState(null);
  const [showIncomingCall, setShowIncomingCall] = useState(false);
  const [ringDuration, setRingDuration] = useState(0);
  const [isProcessingIncomingCall, setIsProcessingIncomingCall] = useState(false); // 🔧 Protection contre appels multiples
  const isProcessingIncomingCallRef = useRef(false);
  const showIncomingCallRef = useRef(false);
  const callStateRef = useRef('idle');
  const processedIncomingCallIdsRef = useRef(new Set());

  // 🔧 NOUVEAU : État pour stocker les infos de l'appelant
  const [activeCallTarget, setActiveCallTarget] = useState(null);

  // 🔧 NOUVEAU : État pour stocker les données complètes de l'utilisateur cible
  const [targetUserFullData, setTargetUserFullData] = useState(null);

  // 🔧 NOUVEAU : État pour les caméras disponibles
  const [availableCameras, setAvailableCameras] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);

  // États et refs pour la sonnerie
  const [isRinging, setIsRinging] = useState(false);
  const ringtoneRef = useRef(null);

  // 🔧 NOUVEAU : Mode audio uniquement
  const [audioOnlyMode, setAudioOnlyMode] = useState(false);
  // Normalise les erreurs (objets, chaînes ou inconnues) en texte lisible
  const normalizeError = useCallback((err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }, []);

  // Refs pour les éléments vidéo
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const waitingStartTimeRef = useRef(null); // Nouveau : démarrage du temps d'attente
  const controlsTimeoutRef = useRef(null);
  const incomingCallListenerRef = useRef(null);
  const ringTimerRef = useRef(null);
  const waitingTimerRef = useRef(null); // Nouveau : timer pour l'attente

  // Fonction pour jouer la sonnerie
  const playRingtone = useCallback(async () => {
    if (ringtoneRef.current && !isRinging) {
      try {
        // Vérifier que l'élément audio est prêt
        if (ringtoneRef.current.readyState === 0) {
          console.log('[VideoCall] Chargement de la sonnerie...');
          await new Promise((resolve) => {
            ringtoneRef.current.addEventListener('loadeddata', resolve, { once: true });
            ringtoneRef.current.load();
          });
        }

        ringtoneRef.current.currentTime = 0;
        ringtoneRef.current.loop = true;
        ringtoneRef.current.volume = 0.7; // Volume modéré

        // Tenter de jouer avec gestion des autorisations
        const playPromise = ringtoneRef.current.play();

        if (playPromise !== undefined) {
          await playPromise;
          setIsRinging(true);
          console.log('[VideoCall] Sonnerie démarrée avec succès');
        }
      } catch (error) {
        console.error('[VideoCall] Erreur lecture sonnerie:', error);

        // Fallback 1 : vibration mobile après une interaction utilisateur.
        if (
          typeof navigator.vibrate === 'function' &&
          navigator.userActivation?.hasBeenActive
        ) {
          console.log('[VideoCall] Fallback: vibration mobile');
          navigator.vibrate([1000, 500, 1000, 500, 1000]);
        }

        // Fallback 2: Notification système si autorisée
        if ('Notification' in window && Notification.permission === 'granted') {
          console.log('[VideoCall] Fallback: notification système');
          new Notification('Appel vidéo entrant', {
            body: 'Vous recevez un appel vidéo',
            icon: '/images/MovieFrenzy.png',
            tag: 'video-call'
          });
        }

        // Fallback 3: Alerte sonore avec oscillateur Web Audio API
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);

          oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
          gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);

          oscillator.start();
          setTimeout(() => oscillator.stop(), 1000);

          console.log('[VideoCall] Fallback: signal audio généré');
        } catch (audioError) {
          console.error('[VideoCall] Fallback audio échoué:', audioError);
        }
      }
    }
  }, [isRinging]);

  // Fonction pour arrêter la sonnerie
  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current && isRinging) {
      try {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
        setIsRinging(false);
        console.log('[VideoCall] Sonnerie arrêtée');
      } catch (error) {
        console.error('[VideoCall] Erreur arrêt sonnerie:', error);
      }
    }

    // Arrêter les vibrations seulement après une interaction utilisateur.
    if (
      typeof navigator.vibrate === 'function' &&
      navigator.userActivation?.hasBeenActive
    ) {
      navigator.vibrate(0);
    }
  }, [isRinging]);

  // Synchroniser les refs "anti stale state"
  useEffect(() => {
    isProcessingIncomingCallRef.current = isProcessingIncomingCall;
  }, [isProcessingIncomingCall]);

  useEffect(() => {
    showIncomingCallRef.current = showIncomingCall;
  }, [showIncomingCall]);

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  const handleIncomingCallDetected = useCallback((callData) => {
    if (!callData?.id) return;

    // 🔧 Protection contre les appels multiples / doublons
    if (isProcessingIncomingCallRef.current) return;
    if (showIncomingCallRef.current || callStateRef.current !== 'idle') return;
    if (processedIncomingCallIdsRef.current.has(callData.id)) return;

    isProcessingIncomingCallRef.current = true;
    setIsProcessingIncomingCall(true);

    processedIncomingCallIdsRef.current.add(callData.id);

    setIncomingCall(callData);
    setShowIncomingCall(true);
    setRingDuration(0);

    // Démarrer la sonnerie personnalisée
    playRingtone();

    // Démarrer le timer de sonnerie
    if (ringTimerRef.current) {
      clearInterval(ringTimerRef.current);
      ringTimerRef.current = null;
    }
    ringTimerRef.current = setInterval(() => {
      setRingDuration(prev => prev + 1000);
    }, 1000);
  }, [playRingtone]);

  // Fonction pour récupérer le messageCount d'un utilisateur
  const fetchUserMessageCount = useCallback(async (userId) => {
    if (!userId) return 0;

    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        let messageCount = userData.messageCount || 0;

        // Si pas de messageCount stocké, calculer depuis les posts du forum
        if (!messageCount) {
          const postsQuery = query(collection(db, 'posts'), where('authorId', '==', userId));
          const postsSnapshot = await getDocs(postsQuery);
          messageCount = postsSnapshot.size;

          // Compter aussi les réponses dans les posts
          for (const postDoc of postsSnapshot.docs) {
            const repliesQuery = query(collection(db, 'posts', postDoc.id, 'replies'));
            const repliesSnapshot = await getDocs(repliesQuery);
            messageCount += repliesSnapshot.size;
          }
        }

        return messageCount;
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du messageCount:', error);
    }

    return 0;
  }, []);

  // Terminer l'appel
  const handleCallEnd = useCallback(async () => {
    const clearTimers = () => {
      if (waitingTimerRef.current) {
        clearInterval(waitingTimerRef.current);
        waitingTimerRef.current = null;
      }
      if (ringTimerRef.current) {
        clearInterval(ringTimerRef.current);
        ringTimerRef.current = null;
      }
    };

    try {
      const duration = callStartTimeRef.current ? Date.now() - callStartTimeRef.current : 0;

      clearTimers();
      stopRingtone();

      // 🔧 NOUVEAU : Si on est en attente (l'appelé n'a pas encore répondu)
      if (callState === 'waiting' && mode === 'outgoing' && targetUser) {
        console.log('[VideoCall] 📞 Appel annulé avant réponse');

        // Créer une notification d'appel manqué pour l'appelé
        await callNotificationService.createMissedCallNotification(
          currentUserId,
          {
            username: currentUsername,
            avatar: currentAvatar
          },
          targetUser.userId || targetUser.uid,
          waitingDuration
        );
      }

      // 🔧 NOUVEAU : Notifier l'autre utilisateur de la fermeture
      if (webRTCService.currentCallId) {
        const callDocRef = doc(db, 'calls', webRTCService.currentCallId);
        await updateDoc(callDocRef, {
          status: 'ended',
          endedBy: currentUserId,
          endedAt: serverTimestamp()
        });
      }

      await webRTCService.hangupCall();

      // Créer notification d'appel terminé si l'appel a duré assez longtemps
      if (duration > 5000 && targetUser) {
        await callNotificationService.createCallEndedNotification(
          currentUserId,
          {
            username: currentUsername,
            avatar: currentAvatar
          },
          targetUser.userId || targetUser.uid,
          duration
        );
      }

      // Réinitialiser les états
      setCallState('ended');
      setLocalStream(null);
      setRemoteStream(null);
      setError(null);
      setWaitingDuration(0);
      setForceShowInterface(false);
      setActiveCallTarget(null);
      callStartTimeRef.current = null;
      waitingStartTimeRef.current = null;

      // Fermer après un délai
      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);

    } catch (error) {
      console.error('[VideoCall] Erreur fin appel:', error);
    } finally {
      clearTimers();
      stopRingtone();
      // Double sécurité : nettoyer si hangup a échoué
      webRTCService.cleanup?.().catch((cleanupError) => {
        console.error('[VideoCall] Erreur cleanup final:', cleanupError);
      });
      processedIncomingCallIdsRef.current.clear();
      isProcessingIncomingCallRef.current = false;
      showIncomingCallRef.current = false;
    }
  }, [onClose, currentUserId, currentUsername, currentAvatar, targetUser, stopRingtone, callState, mode, waitingDuration]);

  // Récupérer les messageCounts au montage et quand les utilisateurs changent
  useEffect(() => {
    const fetchMessageCounts = async () => {
      // Utiliser activeCallTarget ou targetUser
      const target = activeCallTarget || targetUser;

      if (target) {
        const targetCount = await fetchUserMessageCount(target.userId || target.uid);
        setTargetUserMessageCount(targetCount);
      }

      if (currentUserId) {
        const currentCount = await fetchUserMessageCount(currentUserId);
        setCurrentUserMessageCount(currentCount);
      }
    };

    fetchMessageCounts();
  }, [targetUser, activeCallTarget, currentUserId, fetchUserMessageCount]);

  // 🔧 NOUVEAU : Récupérer les données complètes de l'utilisateur cible (incluant pays/ville)
  useEffect(() => {
    const fetchTargetUserFullData = async () => {
      const target = activeCallTarget || targetUser;
      if (!target || !target.userId && !target.uid) return;

      try {
        const userId = target.userId || target.uid;
        const userDocRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('[VideoCall] 🌍 Données complètes utilisateur récupérées:', {
            username: userData.username,
            country: userData.country,
            city: userData.city,
            state: userData.state
          });

          // Mettre à jour les données de l'utilisateur cible avec les infos complètes
          if (activeCallTarget) {
            setActiveCallTarget(prev => ({
              ...prev,
              ...userData
            }));
          }

          // Stocker les données complètes dans un état dédié
          setTargetUserFullData(userData);
        }
      } catch (error) {
        console.error('[VideoCall] Erreur récupération données complètes:', error);
      }
    };

    fetchTargetUserFullData();
  }, [targetUser, activeCallTarget]);

  // 🔧 NOUVEAU : Récupérer la liste des caméras disponibles
  useEffect(() => {
    const fetchCameras = async () => {
      try {
        const cameras = await webRTCService.getAvailableCameras();
        setAvailableCameras(cameras);
        console.log('[VideoCall] 📹 Caméras détectées:', cameras.length);
      } catch (error) {
        console.error('[VideoCall] Erreur récupération caméras:', error);
      }
    };

    // Récupérer les caméras quand un appel est actif
    if (callState !== 'idle' && callState !== 'ended') {
      fetchCameras();
    }
  }, [callState]);

  // Configurer les callbacks du service WebRTC
  useEffect(() => {
    webRTCService.onRemoteStreamReceived = (stream) => {
      console.log('[VideoCall] 🎥 Stream distant reçu');
      console.log('[VideoCall] 🎥 Stream tracks:', {
        audioTracks: stream.getAudioTracks().length,
        videoTracks: stream.getVideoTracks().length,
        active: stream.active
      });

      // 🔧 NOUVEAU : Vérifier les tracks vidéo
      const videoTracks = stream.getVideoTracks();
      if (videoTracks.length > 0) {
        videoTracks.forEach((track, index) => {
          console.log(`[VideoCall] 🎥 Track vidéo ${index}:`, {
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
            label: track.label,
            settings: track.getSettings()
          });
        });
      } else {
        console.warn('[VideoCall] ⚠️ Aucune track vidéo dans le stream distant !');
      }

      setRemoteStream(stream);
      if (remoteVideoRef.current) {
        console.log('[VideoCall] 🎥 Assignation stream distant à l\'élément vidéo');
        remoteVideoRef.current.srcObject = stream;

        // 🔧 NOUVEAU : Gestion robuste de la lecture vidéo
        const playVideo = async () => {
          try {
            await remoteVideoRef.current.play();
            console.log('[VideoCall] 🎥 ✅ Lecture vidéo distante démarrée');
          } catch (error) {
            console.error('[VideoCall] 🎥 Erreur lecture vidéo distante:', error);

            // 🔧 Si erreur de lecture, essayer en mode muet
            if (error.name === 'NotAllowedError') {
              remoteVideoRef.current.muted = true;
              try {
                await remoteVideoRef.current.play();
                console.log('[VideoCall] 🎥 Lecture démarrée en mode muet');
              } catch (retryError) {
                console.error('[VideoCall] 🎥 Échec même en mode muet:', retryError);
                setError('Problème d\'affichage vidéo. Essayez de désactiver l\'accélération matérielle.');
              }
            }
          }
        };

        playVideo();
      }
    };

    webRTCService.onCallStateChange = (state) => {
      console.log('[VideoCall] État changé:', state);
      setCallState(state);

      // Arrêter la sonnerie quand l'appel est connecté
      if (state === 'connected') {
        stopRingtone();
      }
    };

    webRTCService.onError = (rawError) => {
      console.error('[VideoCall] Erreur WebRTC:', rawError);
      const errorMessage = normalizeError(rawError);

      // 🔧 NOUVEAU : Gestion spécifique des erreurs vidéo
      if (errorMessage.includes('Could not start video source') ||
        errorMessage.includes('NotReadableError') ||
        errorMessage.includes('NotFoundError')) {
        console.log('[VideoCall] 🎤 Problème caméra détecté, proposition mode audio');
        setError('Problème avec votre caméra. Voulez-vous continuer en mode audio uniquement ?');
        setAudioOnlyMode(true);
        return;
      }

      // 🔧 NOUVEAU : Détecter problème d'accélération matérielle
      if (errorMessage.includes('hardware acceleration') || errorMessage.includes('GPU')) {
        setError('Problème d\'affichage détecté. Désactivez l\'accélération matérielle dans les paramètres de votre navigateur.');
        return;
      }

      setError(errorMessage);
    };

    // 🔧 NOUVEAU : Écouter les changements d'état de l'appel en cours
    let unsubscribeCallStatus = null;

    if (webRTCService.currentCallId) {
      const callDocRef = doc(db, 'calls', webRTCService.currentCallId);
      unsubscribeCallStatus = onSnapshot(callDocRef, (snapshot) => {
        const data = snapshot.data();
        if (data) {
          // Si l'autre utilisateur a fermé l'appel
          if (data.status === 'ended' && data.endedBy && data.endedBy !== currentUserId) {
            console.log('[VideoCall] 🔚 L\'autre utilisateur a fermé l\'appel');
            handleCallEnd();
          }
        }
      });
    }

    return () => {
      webRTCService.onRemoteStreamReceived = null;
      webRTCService.onCallStateChange = null;
      webRTCService.onError = null;

      if (unsubscribeCallStatus) {
        unsubscribeCallStatus();
      }
    };
  }, [currentUserId, handleCallEnd, stopRingtone, normalizeError]);

  // Écouter les appels entrants
  useEffect(() => {
    if (!currentUserId) {
      console.log('[VideoCall] ❌ Pas de currentUserId pour écouter les appels');

      // 🧹 NETTOYAGE COMPLET si pas d'utilisateur connecté
      if (incomingCallListenerRef.current) {
        incomingCallListenerRef.current();
        incomingCallListenerRef.current = null;
      }
      processedIncomingCallIdsRef.current.clear();
      setShowIncomingCall(false);
      setIncomingCall(null);
      setIsProcessingIncomingCall(false); // 🔧 Remettre le verrou à false
      isProcessingIncomingCallRef.current = false;
      setCallState('idle');
      callStateRef.current = 'idle';
      setError(null);
      stopRingtone();

      return;
    }

    console.log('[VideoCall] 🎯 Configuration listener pour userId:', currentUserId);

    const setupIncomingCallListener = async () => {
      try {
        console.log('[VideoCall] 📡 Démarrage listener appels entrants...');

        incomingCallListenerRef.current = await webRTCService.checkIncomingCalls(
          currentUserId,
          (callData) => {
            console.log('[VideoCall] 🔔 APPEL ENTRANT DÉTECTÉ !', callData);
            console.log('[VideoCall] 📞 callerId:', callData.callerId);
            console.log('[VideoCall] 📞 calleeId:', callData.calleeId);
            console.log('[VideoCall] 📞 callerData:', callData.callerData);
            console.log('[VideoCall] 📞 status:', callData.status);

            handleIncomingCallDetected(callData);
          }
        );

        console.log('[VideoCall] ✅ Listener appels entrants configuré avec succès');
      } catch (error) {
        console.error('[VideoCall] ❌ Erreur listener appels entrants:', error);
        console.error('[VideoCall] ❌ Détails erreur:', error.message);
        console.error('[VideoCall] ❌ Stack:', error.stack);
      }
    };

    setupIncomingCallListener();

    return () => {
      console.log('[VideoCall] 🧹 Nettoyage listeners appels entrants');
      if (incomingCallListenerRef.current) {
        incomingCallListenerRef.current();
        incomingCallListenerRef.current = null;
      }
      if (ringTimerRef.current) {
        clearInterval(ringTimerRef.current);
        ringTimerRef.current = null;
      }
      if (waitingTimerRef.current) {
        clearInterval(waitingTimerRef.current);
        waitingTimerRef.current = null;
      }
      // Arrêter la sonnerie au démontage
      stopRingtone();
    };
  }, [currentUserId, handleIncomingCallDetected, stopRingtone]);

  // 🔧 Fallback: polling des appels entrants (mobile/sleep/échecs onSnapshot)
  useEffect(() => {
    if (!currentUserId) return;

    let pollInterval;
    const poll = async () => {
      try {
        if (isProcessingIncomingCallRef.current) return;
        if (showIncomingCallRef.current) return;
        if (callStateRef.current !== 'idle') return;

        const q = query(
          collection(db, 'calls'),
          where('calleeId', '==', currentUserId),
          where('status', '==', 'ringing')
        );

        const snapshot = await getDocs(q);
        snapshot.forEach((docSnap) => {
          const callData = { id: docSnap.id, ...docSnap.data() };
          handleIncomingCallDetected(callData);
        });
      } catch (pollError) {
        console.error('[VideoCall] ❌ Erreur polling appels entrants:', pollError);
      }
    };

    poll();
    pollInterval = setInterval(poll, 5000);

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [currentUserId, handleIncomingCallDetected]);

  // Démarrer un appel sortant
  const startOutgoingCall = useCallback(async () => {
    if (!targetUser || !currentUserId) {
      setError('Données utilisateur manquantes');
      return;
    }

    try {
      setCallState('waiting'); // État d'attente pour l'appelant
      setError(null);
      waitingStartTimeRef.current = Date.now();

      // Démarrer le timer d'attente pour l'appelant
      waitingTimerRef.current = setInterval(() => {
        setWaitingDuration(prev => prev + 1000);
      }, 1000);

      // Lancer immédiatement la sonnerie côté appelant
      playRingtone();

      const callId = await webRTCService.createCall(
        currentUserId,
        targetUser.userId || targetUser.uid,
        {
          username: currentUsername,
          avatar: currentAvatar
        }
      );

      console.log('[VideoCall] ✅ Appel créé:', callId);

      // 🔧 NOUVEAU : Surveiller les changements de statut de l'appel
      const callDocRef = doc(db, 'calls', callId);
      const unsubscribeCallStatus = onSnapshot(callDocRef, (snapshot) => {
        const data = snapshot.data();
        if (data && data.status === 'connected') {
          console.log('[VideoCall] 🎉 Appel accepté par le destinataire !');
          // Arrêter la sonnerie côté appelant
          stopRingtone();
          // Arrêter le timer d'attente
          if (waitingTimerRef.current) {
            clearInterval(waitingTimerRef.current);
            waitingTimerRef.current = null;
          }
          // Passer en état connecté
          setCallState('connected');
          callStartTimeRef.current = Date.now();
          // Se désabonner après avoir reçu la connexion
          unsubscribeCallStatus();
        }
      });

      // 🔧 CRITIQUE : Configurer le stream local immédiatement côté appelant
      if (webRTCService.localStream) {
        console.log('[VideoCall] 🎥 Configuration stream local appelant');
        console.log('[VideoCall] 🎥 Stream local tracks:', {
          audioTracks: webRTCService.localStream.getAudioTracks().length,
          videoTracks: webRTCService.localStream.getVideoTracks().length,
          active: webRTCService.localStream.active
        });
        setLocalStream(webRTCService.localStream);
        if (localVideoRef.current) {
          console.log('[VideoCall] 🎥 Assignation stream local appelant à l\'élément vidéo');
          localVideoRef.current.srcObject = webRTCService.localStream;
          localVideoRef.current.play().catch((error) => {
            console.error('[VideoCall] 🎥 Erreur lecture vidéo locale:', error);
          });
        }
      }

      // Créer notification d'appel entrant pour l'appelé
      await callNotificationService.createIncomingCallNotification(
        currentUserId,
        {
          username: currentUsername,
          avatar: currentAvatar
        },
        targetUser.userId || targetUser.uid
      );

      console.log('[VideoCall] 📞 Notification envoyée au destinataire');

    } catch (error) {
      console.error('[VideoCall] ❌ Erreur création appel:', error);
      setError('Impossible de démarrer l\'appel: ' + error.message);
      setCallState('idle');
      stopRingtone(); // Arrêter la sonnerie en cas d'erreur
      if (waitingTimerRef.current) {
        clearInterval(waitingTimerRef.current);
      }
    }
  }, [targetUser, currentUserId, currentUsername, currentAvatar, playRingtone, stopRingtone]);

  // Refuser un appel entrant
  const rejectIncomingCall = useCallback(async () => {
    if (!incomingCall) return;

    try {
      // Arrêter la sonnerie immédiatement
      stopRingtone();

      await webRTCService.rejectCall(incomingCall.id, 'Appel refusé');

      // Créer notification d'appel manqué pour l'appelant
      await callNotificationService.createMissedCallNotification(
        currentUserId,
        {
          username: currentUsername,
          avatar: currentAvatar
        },
        incomingCall.callerId,
        ringDuration
      );

      setShowIncomingCall(false);
      setIncomingCall(null);
      setIsProcessingIncomingCall(false); // 🔧 Libérer le verrou
      isProcessingIncomingCallRef.current = false;
      processedIncomingCallIdsRef.current.delete(incomingCall.id);
      if (ringTimerRef.current) {
        clearInterval(ringTimerRef.current);
        ringTimerRef.current = null;
      }
    } catch (error) {
      console.error('[VideoCall] Erreur refus appel:', error);
      setIsProcessingIncomingCall(false); // 🔧 Libérer le verrou même en cas d'erreur
      isProcessingIncomingCallRef.current = false;
    }
  }, [incomingCall, currentUserId, currentUsername, currentAvatar, ringDuration, stopRingtone]);

  // 🔧 NOUVEAU : Surveiller si l'appel entrant est annulé par l'appelant
  useEffect(() => {
    if (!incomingCall || !showIncomingCall) return;

    const callDocRef = doc(db, 'calls', incomingCall.id);
    const unsubscribe = onSnapshot(callDocRef, async (snapshot) => {
      const data = snapshot.data();

      if (!data) {
        // L'appel a été supprimé (l'appelant a raccroché)
        console.log('[VideoCall] 📞 Appel annulé par l\'appelant');

        // Arrêter la sonnerie
        stopRingtone();

        // Créer une notification d'appel manqué
        await callNotificationService.createMissedCallNotification(
          incomingCall.callerId,
          incomingCall.callerData,
          currentUserId,
          ringDuration
        );

        // Fermer l'interface d'appel entrant
        setShowIncomingCall(false);
        setIncomingCall(null);
        setIsProcessingIncomingCall(false);
        isProcessingIncomingCallRef.current = false;
        processedIncomingCallIdsRef.current.delete(incomingCall.id);
        if (ringTimerRef.current) {
          clearInterval(ringTimerRef.current);
          ringTimerRef.current = null;
        }
      } else if (data.status === 'ended' || data.status === 'rejected') {
        // L'appel a été terminé ou rejeté
        console.log('[VideoCall] 📞 Appel terminé/rejeté, statut:', data.status);

        // Arrêter la sonnerie
        stopRingtone();

        // Si l'appel n'a pas été accepté, créer une notification d'appel manqué
        if (data.status === 'ended' && !data.answeredAt) {
          await callNotificationService.createMissedCallNotification(
            incomingCall.callerId,
            incomingCall.callerData,
            currentUserId,
            ringDuration
          );
        }

        // Fermer l'interface
        setShowIncomingCall(false);
        setIncomingCall(null);
        setIsProcessingIncomingCall(false);
        isProcessingIncomingCallRef.current = false;
        processedIncomingCallIdsRef.current.delete(incomingCall.id);
        if (ringTimerRef.current) {
          clearInterval(ringTimerRef.current);
          ringTimerRef.current = null;
        }
      }
    });

    return () => unsubscribe();
  }, [incomingCall, showIncomingCall, currentUserId, ringDuration, stopRingtone]);

  // Accepter un appel entrant
  const acceptIncomingCall = useCallback(async () => {
    if (!incomingCall) return;

    try {
      // Arrêter la sonnerie immédiatement
      stopRingtone();

      // 🔧 CRITIQUE : Configurer l'état AVANT toute autre action
      setCallState('connecting');
      setForceShowInterface(true); // Forcer l'affichage de l'interface

      // 🔧 CRITIQUE : Définir les infos de l'appelant AVANT de masquer l'interface d'appel
      if (incomingCall.callerData) {
        setActiveCallTarget({
          userId: incomingCall.callerId,
          username: incomingCall.callerData.username,
          avatar: incomingCall.callerData.avatar,
          displayName: incomingCall.callerData.username
        });
        console.log('[VideoCall] 📞 Configuration activeCallTarget:', incomingCall.callerData);
      }

      // 🔧 CRITIQUE : Masquer l'interface d'appel entrant APRÈS avoir configuré l'état
      setShowIncomingCall(false);
      setIsProcessingIncomingCall(false);
      isProcessingIncomingCallRef.current = false;
      if (ringTimerRef.current) {
        clearInterval(ringTimerRef.current);
        ringTimerRef.current = null;
      }

      console.log('[VideoCall] 🎯 Acceptation appel en cours...');

      // Accepter l'appel via WebRTC
      await webRTCService.acceptCall(incomingCall.id, {
        username: currentUsername,
        avatar: currentAvatar
      });

      console.log('[VideoCall] ✅ Appel accepté avec succès');

      // Synchroniser le stream local depuis le service WebRTC
      if (webRTCService.localStream) {
        setLocalStream(webRTCService.localStream);
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = webRTCService.localStream;
          try {
            await localVideoRef.current.play();
            console.log('[VideoCall] 🎥 Stream local configuré avec succès');
          } catch (playError) {
            console.error('[VideoCall] Erreur lecture vidéo locale après acceptation:', playError);
          }
        }
      }

      // Supprimer la notification d'appel entrant
      await callNotificationService.removeIncomingCallNotification(
        currentUserId,
        incomingCall.callerId
      );

      // 🔧 CRITIQUE : Vérifier et configurer le stream local du service WebRTC
      if (webRTCService.localStream && !localStream) {
        console.log('[VideoCall] 🎥 Récupération du stream depuis WebRTC service');
        setLocalStream(webRTCService.localStream);
      }

      // Démarrer le timer de durée d'appel
      callStartTimeRef.current = Date.now();

    } catch (error) {
      console.error('[VideoCall] ❌ Erreur acceptation appel:', error);
      // 🔧 Ne pas refermer l'interface : afficher l'erreur et permettre un retry
      setError('Impossible d\'accepter l\'appel: ' + normalizeError(error));
      setIsProcessingIncomingCall(false);
      isProcessingIncomingCallRef.current = false;
      setShowIncomingCall(false);
      setForceShowInterface(true);
      setCallState('waiting');
    }
  }, [incomingCall, currentUserId, currentUsername, currentAvatar, stopRingtone, normalizeError]);

  // Timer pour la durée de l'appel une fois connecté
  useEffect(() => {
    let durationInterval;

    if (callState === 'connected' && callStartTimeRef.current) {
      durationInterval = setInterval(() => {
        setCallDuration(Date.now() - callStartTimeRef.current);
      }, 1000);
    }

    return () => {
      if (durationInterval) {
        clearInterval(durationInterval);
      }
    };
  }, [callState]);

  // Auto-cacher les contrôles après inactivité
  useEffect(() => {
    const resetControlsTimeout = () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }

      setShowControls(true);

      if (callState === 'connected') {
        controlsTimeoutRef.current = setTimeout(() => {
          setShowControls(false);
        }, 3000);
      }
    };

    resetControlsTimeout();

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [callState]);

  // Démarrer l'appel automatiquement selon le mode
  useEffect(() => {
    if (isOpen && mode === 'outgoing' && targetUser && callState === 'idle') {
      startOutgoingCall();
    }
  }, [isOpen, mode, targetUser, callState, startOutgoingCall]);

  // Gestion de la souris pour l'affichage des contrôles
  const handleMouseMove = useCallback(() => {
    if (callState === 'connected') {
      setShowControls(true);

      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }

      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [callState]);

  // Fonction pour basculer le mode muet
  const toggleMute = useCallback(async () => {
    try {
      await webRTCService.toggleMute();
      setIsMuted(prev => !prev);
    } catch (error) {
      console.error('[VideoCall] Erreur toggle mute:', error);
    }
  }, []);

  // Basculer la vidéo
  const toggleVideo = useCallback(() => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
        webRTCService.toggleVideo();
      }
    }
  }, [localStream]);

  // 🔧 NOUVEAU : Changer de caméra
  const switchCamera = useCallback(async () => {
    try {
      if (availableCameras.length <= 1) {
        console.log('[VideoCall] Une seule caméra disponible');
        return;
      }

      const nextIndex = (currentCameraIndex + 1) % availableCameras.length;
      const nextCamera = availableCameras[nextIndex];

      console.log('[VideoCall] 🔄 Changement de caméra:', nextCamera.label);

      const newStream = await webRTCService.switchCamera(nextCamera.deviceId);
      setLocalStream(newStream);
      setCurrentCameraIndex(nextIndex);

      // Mettre à jour l'élément vidéo
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = newStream;
      }
    } catch (error) {
      console.error('[VideoCall] Erreur changement caméra:', error);
      setError('Impossible de changer de caméra');
    }
  }, [availableCameras, currentCameraIndex]);

  // Fonction pour basculer le plein écran
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Fonctions pour formater la durée
  const formatDuration = (duration) => {
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatWaitingDuration = (duration) => {
    const seconds = Math.floor(duration / 1000);
    return `${seconds}s`;
  };

  // 🔧 NOUVEAU : Relancer l'appel en mode audio uniquement
  const retryAudioOnly = useCallback(async () => {
    try {
      setError(null);
      setAudioOnlyMode(true);

      // Nettoyer l'appel actuel
      await webRTCService.cleanup();

      // Réinitialiser WebRTC avec audio uniquement
      await webRTCService.initializePeerConnection();
      await webRTCService.getUserMedia({ video: false, audio: true });

      // Relancer l'appel selon le rôle
      if (incomingCall) {
        // Si c'était un appel entrant, l'accepter à nouveau
        await acceptIncomingCall();
      } else if (targetUser) {
        // Si c'était un appel sortant, le relancer
        await startOutgoingCall();
      }
    } catch (error) {
      console.error('[VideoCall] Erreur retry audio uniquement:', error);
      setError('Impossible de démarrer l\'appel audio');
    }
  }, [incomingCall, targetUser, acceptIncomingCall, startOutgoingCall]);

  // 🔧 NOUVEAU : Effet pour déboguer les streams
  useEffect(() => {
    if (localStream) {
      console.log('[VideoCall] 📹 LOCAL STREAM ACTIF:', {
        id: localStream.id,
        active: localStream.active,
        audioTracks: localStream.getAudioTracks().map(t => ({
          enabled: t.enabled,
          muted: t.muted,
          readyState: t.readyState,
          label: t.label
        })),
        videoTracks: localStream.getVideoTracks().map(t => ({
          enabled: t.enabled,
          muted: t.muted,
          readyState: t.readyState,
          label: t.label,
          settings: t.getSettings()
        }))
      });
    }

    if (remoteStream) {
      console.log('[VideoCall] 📹 REMOTE STREAM ACTIF:', {
        id: remoteStream.id,
        active: remoteStream.active,
        audioTracks: remoteStream.getAudioTracks().map(t => ({
          enabled: t.enabled,
          muted: t.muted,
          readyState: t.readyState,
          label: t.label
        })),
        videoTracks: remoteStream.getVideoTracks().map(t => ({
          enabled: t.enabled,
          muted: t.muted,
          readyState: t.readyState,
          label: t.label,
          settings: t.getSettings()
        }))
      });
    }
  }, [localStream, remoteStream]);

  // 🔧 NOUVEAU : Fonction de débogage vidéo (désactivée par défaut pour éviter le spam)
  useEffect(() => {
    const enableVerboseDebug = false;
    if (!enableVerboseDebug) return undefined;

    const debugInterval = setInterval(() => {
      if (localVideoRef.current) {
        const localVideo = localVideoRef.current;
        console.log('[VideoCall] 📹 DEBUG LOCAL VIDEO:', {
          srcObject: localVideo.srcObject ? 'présent' : 'absent',
          readyState: localVideo.readyState,
          networkState: localVideo.networkState,
          videoWidth: localVideo.videoWidth,
          videoHeight: localVideo.videoHeight,
          paused: localVideo.paused,
          muted: localVideo.muted
        });
      }

      if (remoteVideoRef.current) {
        const remoteVideo = remoteVideoRef.current;
        console.log('[VideoCall] 📹 DEBUG REMOTE VIDEO:', {
          srcObject: remoteVideo.srcObject ? 'présent' : 'absent',
          readyState: remoteVideo.readyState,
          networkState: remoteVideo.networkState,
          videoWidth: remoteVideo.videoWidth,
          videoHeight: remoteVideo.videoHeight,
          paused: remoteVideo.paused,
          muted: remoteVideo.muted
        });
      }
    }, 3000);

    return () => clearInterval(debugInterval);
  }, []);

  // 🔧 NOUVEAU : Effet pour forcer l'affichage des vidéos
  useEffect(() => {
    // Forcer la lecture de la vidéo locale
    if (localStream && localVideoRef.current && localVideoRef.current.srcObject !== localStream) {
      console.log('[VideoCall] 🎥 Mise à jour vidéo locale');
      localVideoRef.current.srcObject = localStream;
      localVideoRef.current.play().catch(e => console.error('[VideoCall] Erreur play local:', e));
    }

    // Forcer la lecture de la vidéo distante
    if (remoteStream && remoteVideoRef.current && remoteVideoRef.current.srcObject !== remoteStream) {
      console.log('[VideoCall] 🎥 Mise à jour vidéo distante');
      remoteVideoRef.current.srcObject = remoteStream;
      remoteVideoRef.current.play().catch(e => console.error('[VideoCall] Erreur play distant:', e));
    }
  }, [localStream, remoteStream]);

  // 🔧 NOUVEAU : Effet pour surveiller le changement d'état de connexion
  useEffect(() => {
    if (callState === 'connected') {
      console.log('[VideoCall] 🎉 APPEL CONNECTÉ ! Vérification des streams...');

      // Vérifier le stream local
      if (webRTCService.localStream && !localStream) {
        console.log('[VideoCall] 📹 Récupération du stream local depuis WebRTC service');
        setLocalStream(webRTCService.localStream);
      }

      // Vérifier le stream distant
      if (webRTCService.remoteStream && !remoteStream) {
        console.log('[VideoCall] 📹 Récupération du stream distant depuis WebRTC service');
        setRemoteStream(webRTCService.remoteStream);
      }

      // Forcer la mise à jour des vidéos après un court délai
      setTimeout(() => {
        if (localVideoRef.current && localStream) {
          console.log('[VideoCall] 🎥 Forçage affichage vidéo locale');
          localVideoRef.current.srcObject = localStream;
          localVideoRef.current.play().catch(e => console.error('[VideoCall] Erreur play local:', e));
        }

        if (remoteVideoRef.current && remoteStream) {
          console.log('[VideoCall] 🎥 Forçage affichage vidéo distante');
          remoteVideoRef.current.srcObject = remoteStream;
          remoteVideoRef.current.play().catch(e => console.error('[VideoCall] Erreur play distant:', e));
        }
      }, 500);
    }
  }, [callState, localStream, remoteStream]);

  // Affichage conditionnel selon l'état
  const incomingNotification = showIncomingCall ? (
    incomingCall ? (
      <IncomingCallNotification
        caller={{
          userId: incomingCall.callerId,
          username: incomingCall.callerData?.username,
          avatar: incomingCall.callerData?.avatar
        }}
        callData={incomingCall}
        onAccept={acceptIncomingCall}
        onReject={rejectIncomingCall}
        isVisible={showIncomingCall}
        ringDuration={ringDuration}
      />
    ) : (
      <div className="mf-incoming-call-overlay">
        <div className="mf-incoming-call-modal">
          <div className="mf-caller-info">
            <h2 className="mf-caller-name">Appel entrant...</h2>
            <p className="mf-call-status">Chargement…</p>
          </div>
        </div>
      </div>
    )
  ) : null;

  const audioElement = (
    <audio
      ref={ringtoneRef}
      preload="auto"
      style={{ display: 'none' }}
    >
      <source src="/futuristic-MovieFrenzy_Call.mp3" type="audio/mpeg" />
      Votre navigateur ne supporte pas l'élément audio.
    </audio>
  );

  const shouldRenderMainUI = (isOpen || forceShowInterface) && !incomingNotification;

  // ✅ MODIFICATION : Ne pas retourner null si isOpen=false mais mode="incoming"
  // mais garder l'élément audio prêt pour la sonnerie.
  if (!shouldRenderMainUI && !incomingNotification && mode !== 'incoming') {
    return audioElement;
  }

  // Si en mode "incoming" mais pas d'appel en cours, retourner uniquement l'audio (listener invisible actif)
  if (mode === 'incoming' && !shouldRenderMainUI && callState === 'idle' && !incomingNotification) {
    return audioElement;
  }

  // Interface principale d'appel vidéo
  try {
    console.log('[VideoCallManager] Rendu de l\'interface avec l\'état:', callState);

    // 🔧 Utiliser les données complètes si disponibles, sinon activeCallTarget ou targetUser
    const currentTarget = targetUserFullData || activeCallTarget || targetUser;
    const safeErrorMessage = normalizeError(error);

    return (
      <>
        {audioElement}

        {incomingNotification ? (
          incomingNotification
        ) : (
          shouldRenderMainUI && (
            <div
              className={`mf-videocall-container ${isFullscreen ? 'fullscreen' : ''}`}
              onMouseMove={handleMouseMove}
            >
              <div className="mf-videocall-overlay">
                <div className={`mf-videocall-window ${callState}`}>

                  {/* Header avec informations d'appel */}
                  <div className={`mf-call-header ${showControls ? 'visible' : ''}`}>
                    <div className="mf-call-info">
                      <span className="mf-peer-name">
                        {currentTarget?.username || currentTarget?.displayName || 'Utilisateur'}
                      </span>
                      {/* 🔧 NOUVEAU : Bandeau timestamp déplacé ici */}
                      {callState === 'connected' && callStartTimeRef.current && (
                        <div className="mf-timestamp-inline">
                          {currentTarget?.country && (
                            <Flag
                              code={getCountryFlag(currentTarget.country)}
                              className="mf-country-flag"
                              fallback={<span className="mf-flag-fallback">🌍</span>}
                            />
                          )}
                          <span className="mf-timestamp-text">
                            {formatTimestampShort(Math.floor(callStartTimeRef.current / 1000), currentTarget)}
                          </span>
                        </div>
                      )}
                      <span className="mf-call-status">
                        {callState === 'waiting' && `En attente de réponse... ${formatWaitingDuration(waitingDuration)}`}
                        {callState === 'ringing' && 'Appel en cours...'}
                        {callState === 'connecting' && 'Connexion...'}
                        {callState === 'ended' && 'Appel terminé'}
                      </span>
                    </div>

                    <button
                      className="mf-close-btn"
                      onClick={handleCallEnd}
                      title="Fermer"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Zone vidéo principale */}
                  <div className="mf-video-container">
                    {/* Effet de particules en arrière-plan */}
                    {callState === 'connected' && (
                      <div className="mf-video-particles">
                        {[...Array(15)].map((_, i) => (
                          <div key={i} className={`mf-video-particle particle-${i}`} />
                        ))}
                      </div>
                    )}

                    {/* Vidéo distante ou placeholder audio */}
                    {remoteStream && !audioOnlyMode ? (
                      <video
                        ref={remoteVideoRef}
                        className="mf-remote-video"
                        autoPlay={true}
                        playsInline={true}
                        muted={false}
                        controls={false}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          backgroundColor: '#000'
                        }}
                        onLoadedMetadata={(e) => {
                          console.log('[VideoCall] 🎥 Métadonnées vidéo distante chargées:', {
                            videoWidth: e.target.videoWidth,
                            videoHeight: e.target.videoHeight,
                            duration: e.target.duration,
                            readyState: e.target.readyState
                          });
                        }}
                        onCanPlay={() => {
                          console.log('[VideoCall] 🎥 Vidéo distante prête à jouer');
                        }}
                        onPlay={() => {
                          console.log('[VideoCall] 🎥 ▶️ Lecture vidéo distante en cours');
                        }}
                        onPlaying={() => {
                          console.log('[VideoCall] 🎥 ▶️ Vidéo distante en lecture active');
                        }}
                        onError={(e) => {
                          console.error('[VideoCall] 🎥 Erreur vidéo distante:', e);
                          console.error('[VideoCall] 🎥 Détails erreur:', {
                            error: e.target.error,
                            networkState: e.target.networkState,
                            readyState: e.target.readyState,
                            currentSrc: e.target.currentSrc
                          });
                          // En cas d'erreur vidéo, proposer le mode audio
                          setError('Problème d\'affichage vidéo. Essayez de désactiver l\'accélération matérielle.');
                        }}
                      />
                    ) : (
                      <div className="mf-video-placeholder">
                        {audioOnlyMode ? (
                          <div className="mf-audio-only-container">
                            <div className="mf-audio-waves">
                              <div className="wave"></div>
                              <div className="wave"></div>
                              <div className="wave"></div>
                              <div className="wave"></div>
                              <div className="wave"></div>
                            </div>
                            <h3>🎤 Mode Audio Uniquement</h3>
                            <p>Problème détecté avec la caméra</p>
                          </div>
                        ) : (
                          <>
                            {currentTarget?.avatar ? (
                              <img
                                src={currentTarget.avatar}
                                alt={currentTarget.username || currentTarget.displayName}
                                className="mf-placeholder-avatar"
                              />
                            ) : (
                              <div className="mf-placeholder-icon">👤</div>
                            )}
                            <Lottie
                              options={{
                                loop: true,
                                autoplay: true,
                                animationData: spiroAnimation,
                                rendererSettings: {
                                  preserveAspectRatio: 'xMidYMid slice'
                                }
                              }}
                              height={200}
                              width={200}
                              style={{ position: 'absolute', opacity: 0.3 }}
                            />
                          </>
                        )}
                      </div>
                    )}

                    {/* Vidéo locale (picture-in-picture) avec drag */}
                    <div className={`mf-local-video ${isVideoOff ? 'video-off' : ''}`}>
                      <div className="mf-video-border"></div>
                      {localStream && !isVideoOff ? (
                        <video
                          ref={localVideoRef}
                          autoPlay={true}
                          muted={true}
                          playsInline={true}
                          controls={false}
                          className="mf-local-video-element"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            backgroundColor: '#000',
                            transform: 'scaleX(-1)'
                          }}
                          onLoadedMetadata={(e) => {
                            console.log('[VideoCall] 🎥 Métadonnées vidéo locale chargées:', {
                              videoWidth: e.target.videoWidth,
                              videoHeight: e.target.videoHeight,
                              readyState: e.target.readyState
                            });
                          }}
                          onLoadedData={() => console.log('[VideoCall] 🎥 ✅ Vidéo locale chargée et prête')}
                          onPlay={() => console.log('[VideoCall] 🎥 ▶️ Lecture vidéo locale démarrée')}
                          onPlaying={() => console.log('[VideoCall] 🎥 ▶️ Vidéo locale en lecture active')}
                          onError={(e) => {
                            console.error('[VideoCall] 🎥 ❌ Erreur vidéo locale:', e);
                            console.error('[VideoCall] 🎥 Détails erreur:', {
                              error: e.target.error,
                              networkState: e.target.networkState,
                              readyState: e.target.readyState
                            });
                          }}
                        />
                      ) : (
                        <div className="mf-local-placeholder">
                          <img
                            src={currentAvatar || '/images/defaultAvatar.png'}
                            alt={currentUsername || 'Vous'}
                            className="mf-simple-avatar"
                            onError={(e) => {
                              e.target.src = '/images/defaultAvatar.png';
                            }}
                          />
                          {isVideoOff && (
                            <div className="mf-video-off-overlay">
                              <span className="mf-video-off-icon">📷</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contrôles d'appel améliorés */}
                  <div className={`mf-call-controls ${showControls ? 'visible' : ''}`}>
                    <div className="mf-controls-wrapper">
                      <div className="mf-controls-group">
                        {/* Bouton microphone */}
                        <button
                          className={`mf-control-btn ${isMuted ? 'muted' : ''}`}
                          onClick={toggleMute}
                          title={isMuted ? 'Activer le micro' : 'Couper le micro'}
                        >
                          <div className="mf-btn-bg"></div>
                          <span className="mf-btn-icon">
                            {isMuted ? (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" fill="currentColor" />
                              </svg>
                            ) : (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14-1.14z" fill="currentColor" />
                              </svg>
                            )}
                          </span>
                        </button>

                        {/* Bouton Vidéo */}
                        <button
                          onClick={toggleVideo}
                          className={`mf-control-btn ${isVideoOff ? 'video-off' : ''}`}
                          title={isVideoOff ? 'Activer la vidéo' : 'Désactiver la vidéo'}
                        >
                          <div className="mf-btn-bg"></div>
                          <span className="mf-btn-icon">
                            {isVideoOff ? (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z" fill="currentColor" />
                              </svg>
                            ) : (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" fill="currentColor" />
                              </svg>
                            )}
                          </span>
                        </button>

                        {/* 🔧 NOUVEAU : Bouton Changer de caméra */}
                        {availableCameras.length > 1 && (
                          <button
                            onClick={switchCamera}
                            className="mf-control-btn"
                            title={`Changer de caméra (${currentCameraIndex + 1}/${availableCameras.length})`}
                          >
                            <div className="mf-btn-bg"></div>
                            <span className="mf-btn-icon">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M20 4h-3.17L15 2H9L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 3c1.63 0 3.06.79 3.98 2H12c-1.66 0-3 1.34-3 3 0 .35.07.69.18 1H7.1c-.06-.32-.1-.66-.1-1 0-2.76 2.24-5 5-5zm0 10c-1.63 0-3.06-.79-3.98-2H12c1.66 0 3-1.34 3-3 0-.35-.07-.69-.18-1h2.08c.07.32.1.66.1 1 0 2.76-2.24 5-5 5z" fill="currentColor" />
                              </svg>
                            </span>
                          </button>
                        )}

                        {/* Bouton Raccrocher */}
                        <button
                          onClick={handleCallEnd}
                          className="mf-control-btn hangup"
                          title="Terminer l'appel"
                        >
                          <div className="mf-btn-bg"></div>
                          <span className="mf-btn-icon">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                              <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" fill="currentColor" />
                            </svg>
                          </span>
                        </button>

                        {/* Bouton plein écran */}
                        <button
                          className="mf-control-btn"
                          onClick={toggleFullscreen}
                          title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
                        >
                          <div className="mf-btn-bg"></div>
                          <span className="mf-btn-icon">
                            {isFullscreen ? (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" fill="currentColor" />
                              </svg>
                            ) : (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" fill="currentColor" />
                              </svg>
                            )}
                          </span>
                        </button>
                      </div>

                      {/* Timer d'appel - Seul indicateur de durée conservé */}
                      {callState === 'connected' && (
                        <div className="mf-call-timer">
                          <span className="mf-timer-icon">⏱️</span>
                          <span className="mf-timer-value">{formatDuration(callDuration)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Indicateur de qualité de connexion */}
                  <div className="mf-connection-indicator">
                    <div className={`mf-connection-bars ${connectionQuality}`}>
                      <div className="bar bar-1"></div>
                      <div className="bar bar-2"></div>
                      <div className="bar bar-3"></div>
                      <div className="bar bar-4"></div>
                    </div>
                  </div>

                  {/* Message d'erreur ou d'avertissement */}
                  {safeErrorMessage && (
                    <div className="mf-error-message">
                      <div className="mf-error-content">
                        <span className="mf-error-icon">⚠️</span>
                        <p>{safeErrorMessage}</p>
                        {safeErrorMessage.includes('accélération matérielle') && (
                          <div className="mf-error-actions">
                            <a
                              href="chrome://settings/system"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mf-error-link"
                            >
                              Ouvrir les paramètres Chrome
                            </a>
                            <button
                              className="mf-error-btn"
                              onClick={() => {
                                setError(null);
                                setAudioOnlyMode(true);
                              }}
                            >
                              Continuer en audio uniquement
                            </button>
                          </div>
                        )}
                        {safeErrorMessage.includes('mode audio uniquement') && (
                          <div className="mf-error-actions">
                            <button
                              className="mf-error-btn primary"
                              onClick={retryAudioOnly}
                            >
                              Oui, continuer en audio
                            </button>
                            <button
                              className="mf-error-btn"
                              onClick={() => setError(null)}
                            >
                              Annuler
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        )}
      </>
    );
  } catch (renderError) {
    console.error('[VideoCallManager] Erreur de rendu:', renderError);

    // Interface de fallback en cas d'erreur
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        color: 'white',
        textAlign: 'center'
      }}>
        <div>
          <h2>Erreur d'appel vidéo</h2>
          <p>Une erreur est survenue lors de l'initialisation de l'appel.</p>
          <p style={{ fontSize: '0.8rem', opacity: 0.7 }}>
            Détails : {renderError.message}
          </p>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#FF3333',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              marginTop: '20px'
            }}
          >
            Fermer
          </button>
        </div>
      </div>
    );
  }
};

export default VideoCallManager;
